import { ChevronsUpDown, Info, LogOut, MessageSquarePlus, UserRound } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useSavePreferences } from "../account/preferences.ts";
import { FeedbackDialog } from "../feedback/FeedbackDialog.tsx";
import { currentLocale, LOCALES, type Locale } from "../i18n/i18n.ts";
import { useMe } from "../me.ts";
import { ACCOUNT_PATH, navigate } from "../router.tsx";
import { useSession } from "../session.tsx";
import { type ThemeChoice, useTheme } from "../theme/theme.tsx";
import { AboutDialog } from "./AboutDialog.tsx";
import { signOut } from "./sign-out.ts";

const THEMES: ThemeChoice[] = ["system", "light", "dark"];

/**
 * The tenant and user menu: who is signed in (the names the API returns, else the e-mail; never
 * an invented name), the theme and language choices, account settings, About and sign out.
 */
export function AccountMenu() {
  const { t } = useTranslation();
  const { csrfToken, roles } = useSession();
  const me = useMe();
  const { choice } = useTheme();
  // Saved to the account too, so the choice follows the user to other browsers.
  const save = useSavePreferences();
  const { isMobile } = useSidebar();
  const [signingOut, setSigningOut] = useState(false);
  const [about, setAbout] = useState(false);
  const [feedback, setFeedback] = useState(false);
  // About hands over to Send feedback: its close must not pull focus back to the menu button.
  const handedOver = useRef(false);
  // The tenant's `feedback.enabled` (in-app-feedback): hidden while unknown or off.
  const canSendFeedback = me.data?.feedback === true;
  // The menu item is gone once the dialog closes: focus returns to the menu button.
  const trigger = useRef<HTMLButtonElement>(null);
  const name = me.data?.displayName ?? me.data?.email ?? null;
  // The tenant's branding display name when set (tenant-branding), else its name.
  const tenant = me.data?.branding?.displayName ?? me.data?.tenantName ?? null;
  const role = roles.includes("tenant_admin")
    ? t("account.roles.tenant_admin")
    : roles.includes("entity_admin")
      ? t("account.roles.entity_admin")
      : t("account.roles.member");

  async function leave() {
    setSigningOut(true);
    await signOut(csrfToken);
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem data-esi-content="people">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              ref={trigger}
              size="lg"
              tooltip={name ?? t("account.menu")}
              className="data-[state=open]:bg-sidebar-accent"
            >
              {/* Named from its content, so the visible name and tenant stay in the accessible
                  name (WCAG 2.5.3); the initials are hidden from it, the purpose is sr-only. */}
              <span className="sr-only">{t("account.menu")}, </span>
              <Avatar name={name ?? ""} />
              <span className="grid min-w-0 flex-1 text-left leading-tight">
                <span className="truncate text-sm font-medium text-fg">
                  {name ?? role}
                  <span className="sr-only">,</span>
                </span>
                <span className="truncate text-xs text-fg-muted">{tenant ?? role}</span>
              </span>
              <ChevronsUpDown aria-hidden="true" className="ml-auto size-4 text-fg-muted" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? "top" : "right"}
            align="end"
            sideOffset={8}
            className="w-64"
          >
            <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
              <span className="text-xs text-fg-muted">{t("account.signedInAs")}</span>
              {name ? <span className="truncate text-sm font-medium text-fg">{name}</span> : null}
              <span className="truncate text-xs text-fg-secondary">
                {[tenant, role].filter(Boolean).join(" · ")}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-medium text-fg-muted">
                {t("account.theme")}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={choice}
                onValueChange={(value) => save.theme(value as ThemeChoice)}
              >
                {THEMES.map((theme) => (
                  <DropdownMenuRadioItem key={theme} value={theme}>
                    {t(`account.themes.${theme}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-medium text-fg-muted">
                {t("account.language")}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={currentLocale()}
                onValueChange={(value) => save.language(value as Locale)}
              >
                {LOCALES.map((locale) => (
                  <DropdownMenuRadioItem key={locale} value={locale} lang={locale}>
                    {t(`account.languages.${locale}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate(ACCOUNT_PATH)}>
              <UserRound aria-hidden="true" strokeWidth={1.75} />
              {t("account.settings")}
            </DropdownMenuItem>
            {canSendFeedback ? (
              <DropdownMenuItem onSelect={() => setFeedback(true)}>
                <MessageSquarePlus aria-hidden="true" strokeWidth={1.75} />
                {t("account.sendFeedback")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={() => setAbout(true)}>
              <Info aria-hidden="true" strokeWidth={1.75} />
              {t("account.about")}
            </DropdownMenuItem>
            <DropdownMenuItem disabled={signingOut} onSelect={() => void leave()}>
              <LogOut aria-hidden="true" strokeWidth={1.75} />
              {signingOut ? t("account.signingOut") : t("account.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <AboutDialog
          open={about}
          onClose={() => setAbout(false)}
          returnFocus={() => {
            const handed = handedOver.current;
            handedOver.current = false;
            return handed ? false : trigger.current;
          }}
          {...(canSendFeedback && {
            onSendFeedback: () => {
              handedOver.current = true;
              setAbout(false);
              setFeedback(true);
            },
          })}
        />
        <FeedbackDialog
          open={feedback}
          onClose={() => setFeedback(false)}
          returnFocus={() => trigger.current}
        />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
